import { Box, Paper, Stack, Typography } from '@mui/material';
import type { GridApiCommunity } from '@mui/x-data-grid/internals';
import { isNotNullOrUndefined, now } from '@vy/utils';
import { VyTableToolbar } from '../../../components/new-design/table/vy-table-toolbar/vy-table-toolbar';
import { formatDate } from '../../../utils/date';
import { ReportMenu } from '../reports-page/reports-page-components/ReportMenu';
import { getReportJobType } from '../reports-page/reports-page-components/ReportsPageContent';
import type { ReportType } from '../reports-page/reports-page-types/report-type';

type ReportTableProps = {
  title: string;
  placeholder?: string;
  exportCsvFileName: string;
  reportCreationDate: Date;
  reportType: ReportType;
  children: React.ReactNode;
  onSearch?: (searchValue: string) => void;
  apiRef: React.MutableRefObject<GridApiCommunity>;
  onExportCsv?: () => void;
  hideMenu?: boolean;
  hideTitle?: boolean;
  topRightSlot?: React.ReactNode;
};

export const ReportTable: React.FC<ReportTableProps> = ({
  title,
  placeholder,
  exportCsvFileName,
  reportCreationDate,
  reportType,
  children,
  onSearch,
  apiRef,
  onExportCsv,
  hideMenu,
  hideTitle,
  topRightSlot,
}) => {
  const handleExport = () => {
    if (isNotNullOrUndefined(onExportCsv)) {
      onExportCsv();
      return;
    }

    apiRef.current.exportDataAsCsv({
      fileName: `${exportCsvFileName} as of ${formatDate(reportCreationDate)} (issued ${formatDate(now())})`,
    });
  };

  return (
    <Paper
      variant="outlined"
      sx={(theme) => ({
        borderRadius: '12px',
        top: '10px',
        boxShadow: theme.shadows[1],
        height: `calc(100vh - ${theme.spacing(22)})`,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        overflow: 'hidden',
      })}
    >
      {topRightSlot && (
        <Box
          sx={{
            position: 'absolute',
            top: 12,
            right: 16,
            zIndex: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'auto' },
          }}
        >
          {topRightSlot}
        </Box>
      )}
      <VyTableToolbar>
        {!hideTitle && (
          <Stack
            direction="row"
            sx={{
              alignItems: "center",
              gap: 2
            }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 500,
                fontSize: "20px"
              }}>
              {title}
            </Typography>
          </Stack>
        )}
        <VyTableToolbar.Actions>
          {isNotNullOrUndefined(onSearch) && isNotNullOrUndefined(placeholder) && (
            <VyTableToolbar.SearchInput
              showLabel={false}
              onChange={(event) => onSearch(event.target.value)}
              defaultValue=""
              placeholder={placeholder}
            />
          )}

          {!hideMenu && (
            <ReportMenu
              reportType={reportType}
              selectedReportJob={getReportJobType(reportType)}
              onExportCsv={handleExport}
              shouldRequestReport={false}
              shouldExportAsCsv
            />
          )}
        </VyTableToolbar.Actions>
      </VyTableToolbar>
      {children}
    </Paper>
  );
};
